import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { PlanComparison } from "@/components/PlanComparison";
import { BrandLogo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  Check, Volume2, LayoutGrid, BarChart3, Clapperboard,
  ArrowRight, Loader2, Sparkles, ShieldCheck, MessageCircle,
} from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/3ff01890-8330-4deb-9a8f-324ff5ebb091/images/057d4cd2177cf6e49d42b0d3cac51e8a4f005bc6f8c18a3e14691d9ae8f514f0.jpeg";
const DOCTOR_IMG = "https://static.prod-images.emergentagent.com/jobs/3ff01890-8330-4deb-9a8f-324ff5ebb091/images/6c0497ce103d6eb227cbaf108b16e39d3c737a1cec51f449ca7c3b158e15c2b1.jpeg";

const brl = (v) => {
  const n = Number(v);
  return `R$ ${n.toLocaleString("pt-BR", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
};

const FEATURES = [
  { icon: Volume2, title: "Chamada por voz", text: "O nome do paciente aparece na TV e a voz chama junto, em português." },
  { icon: LayoutGrid, title: "Filas por sala", text: "Vários profissionais atendendo ao mesmo tempo, cada um com sua fila." },
  { icon: Clapperboard, title: "Mídia na TV", text: "Imagens, vídeos e YouTube/TV ao vivo rodando entre as chamadas." },
  { icon: BarChart3, title: "Relatórios", text: "Tempo médio de espera, horários de pico e atendimentos por sala." },
];

const FAQ = [
  { q: "Preciso instalar algo na TV?", a: "Não. Funciona em qualquer Smart TV, tablet ou computador com navegador. Basta abrir o link do painel e deixar na tela da sala de espera." },
  { q: "O teste grátis pede cartão de crédito?", a: "Não. Você começa o teste de 14 dias sem cartão e só assina se gostar. Pode cancelar quando quiser." },
  { q: "A chamada por voz tem custo?", a: "Não. A voz usa a síntese do próprio navegador, em português, sem mensalidade adicional." },
  { q: "Posso ter vários consultórios chamando ao mesmo tempo?", a: "Sim. Cada sala tem sua própria fila e a TV mostra quem está sendo chamado em cada consultório." },
  { q: "Consigo trocar de plano depois?", a: "Sim. Você pode subir ou descer de plano a qualquer momento, conforme a necessidade da clínica." },
];

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState("monthly");
  const [loadingKey, setLoadingKey] = useState(null);
  const [partnerCode, setPartnerCode] = useState("");

  useEffect(() => {
    axios.get(`${API}/plans`).then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  const subscribe = async (plan) => {
    const lk = billing === "yearly" ? plan.yearly.lookup_key : plan.monthly.lookup_key;
    setLoadingKey(lk);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lk,
        origin_url: window.location.origin,
        partner_code: partnerCode || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setLoadingKey(null);
      if (e.response?.data?.detail === "partner_invalid") {
        alert("Código de parceiro inválido. Verifique e tente novamente.");
      }
    }
  };

  const scrollToPlans = () => document.getElementById("planos")?.scrollIntoView({ behavior: "smooth" });
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-cream text-ink">
      {/* Nav */}
      <nav className="sticky top-0 z-30 bg-cream/80 backdrop-blur-md border-b border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" aria-label="O Seu Chamador">
            <BrandLogo className="h-11" testid="nav-brand-logo" />
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm font-medium text-cappuccino">
            <button onClick={() => scrollTo("features")} className="hover:text-ink transition-colors" data-testid="nav-features">Funcionalidades</button>
            <button onClick={() => scrollTo("planos")} className="hover:text-ink transition-colors" data-testid="nav-pricing">Preços</button>
            <button onClick={() => scrollTo("faq")} className="hover:text-ink transition-colors" data-testid="nav-faq">Dúvidas</button>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" className="rounded-md text-cappuccino" data-testid="nav-login">Entrar</Button>
            </Link>
            <Link to="/login?signup=1">
              <Button className="rounded-md bg-gold hover:bg-gold-hover text-white" data-testid="nav-trial">Teste grátis</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-md bg-accent text-cappuccino px-3 py-1 text-sm font-medium mb-5">
            <Sparkles className="w-4 h-4 text-gold" /> Chamador de pacientes para clínicas
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            A sala de espera da sua clínica, <span className="text-gold">chamada com um clique</span>.
          </h1>
          <p className="text-cappuccino text-lg mt-6 max-w-lg leading-relaxed">
            O nome aparece na TV e a voz chama junto. Filas por sala, relatórios e mídia da clínica — tudo rodando em qualquer Smart TV com navegador.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/login?signup=1">
              <Button className="rounded-md h-12 px-7 bg-gold hover:bg-gold-hover text-white text-base gap-2 group" data-testid="hero-trial">
                Começar teste grátis de 14 dias
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <Button onClick={scrollToPlans} variant="outline" className="rounded-md h-12 px-7 border-ink/20 text-base" data-testid="hero-plans">
              Ver planos
            </Button>
          </div>
          <p className="text-sm text-cappuccino mt-4 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-gold" /> Sem cartão de crédito. Cancele quando quiser.
          </p>
        </div>
        <div className="relative animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="absolute -inset-4 bg-gold/20 rounded-[2rem] blur-2xl" />
          <img src={HERO_IMG} alt="Recepção de clínica" className="relative rounded-[1.5rem] shadow-2xl w-full h-[420px] object-cover" />
        </div>
      </header>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-5 sm:px-8 py-10 scroll-mt-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white border border-border rounded-2xl p-6">
              <div className="w-11 h-11 rounded-xl bg-accent flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-gold" />
              </div>
              <h3 className="font-heading font-bold mb-1.5">{f.title}</h3>
              <p className="text-sm text-cappuccino leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Plans */}
      <section id="planos" className="max-w-6xl mx-auto px-5 sm:px-8 py-16 scroll-mt-20">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Planos simples, sem surpresa</h2>
          <p className="text-cappuccino mt-3">Escolha o plano ideal e comece hoje. Ou faça o teste grátis de 14 dias, sem cartão.</p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-md bg-ink text-cream px-5 py-2.5 text-sm font-medium" data-testid="combo-seal">
            <Sparkles className="w-4 h-4 text-gold shrink-0" />
            <span>Assine junto com o nosso sistema e <strong className="text-gold">ganhe 30% no chamador</strong> — use o código <span className="font-bold text-gold">PARCEIRO</span></span>
          </div>
          <div className="inline-flex items-center gap-1 bg-accent rounded-md p-1 mt-6" data-testid="billing-toggle">
            <button onClick={() => setBilling("monthly")} data-testid="billing-monthly"
              className={`px-5 py-2 rounded-md text-sm font-semibold transition-colors ${billing === "monthly" ? "bg-white text-ink shadow" : "text-cappuccino"}`}>
              Mensal
            </button>
            <button onClick={() => setBilling("yearly")} data-testid="billing-yearly"
              className={`px-5 py-2 rounded-md text-sm font-semibold transition-colors flex items-center gap-2 ${billing === "yearly" ? "bg-white text-ink shadow" : "text-cappuccino"}`}>
              Anual <span className="text-[0.65rem] font-bold bg-gold text-ink px-2 py-0.5 rounded-md">-27%</span>
            </button>
          </div>
        </div>

        <div className="flex justify-center mb-8">
          <div className="w-full max-w-sm">
            <label className="block text-xs font-semibold text-cappuccino mb-1.5 text-center">Tem código de parceiro? (desconto do combo)</label>
            <input data-testid="partner-code-input" value={partnerCode} onChange={(e) => setPartnerCode(e.target.value.toUpperCase())}
              placeholder="Ex: PARCEIRO"
              className="w-full rounded-md border border-border bg-white px-5 h-11 text-sm text-ink outline-none focus:ring-2 focus:ring-gold text-center" />
          </div>
        </div>

        {plans.length === 0 ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {plans.map((plan) => {
              const amount = billing === "yearly" ? plan.yearly.amount : plan.monthly.amount;
              const lk = billing === "yearly" ? plan.yearly.lookup_key : plan.monthly.lookup_key;
              const perMonth = billing === "yearly" ? Math.round(plan.yearly.amount / 12) : plan.monthly.amount;
              const isFree = plan.free;
              const isContact = plan.contact;
              return (
                <div key={plan.id} data-testid={`plan-${plan.id}`}
                  className={`relative rounded-xl p-7 flex flex-col transition-shadow duration-300 ${plan.highlight ? "bg-ink text-cream ring-1 ring-gold hover:shadow-[6px_6px_0_#C5A059]" : "bg-white border border-cappuccino/20 hover:shadow-[6px_6px_0_#2C2420]"}`}>
                  {plan.highlight && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-ink text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-md">
                      Mais popular
                    </span>
                  )}
                  <h3 className="font-heading text-2xl font-bold">{plan.name}</h3>
                  <p className={`text-sm mt-1 ${plan.highlight ? "text-cream/60" : "text-cappuccino"}`}>{plan.tagline}</p>
                  <div className="mt-6 flex items-end gap-1">
                    <span className={`font-heading font-extrabold ${isContact ? "text-3xl whitespace-nowrap" : "text-4xl"}`} data-testid={`price-${plan.id}`}>{isFree ? "Grátis" : isContact ? "Sob consulta" : brl(amount)}</span>
                    {!isFree && !isContact && (
                      <span className={`mb-1 text-sm ${plan.highlight ? "text-cream/60" : "text-cappuccino"}`}>
                        /{billing === "yearly" ? "ano" : "mês"}
                      </span>
                    )}
                  </div>
                  {billing === "yearly" && !isFree && !isContact && (
                    <p className={`text-xs mt-1 ${plan.highlight ? "text-cream/50" : "text-cappuccino"}`}>equivale a {brl(perMonth)}/mês</p>
                  )}
                  {isContact && (
                    <p className="text-xs mt-1 text-cappuccino">Plano personalizado conforme a necessidade da sua clínica.</p>
                  )}
                  {isContact ? (
                    <a href={plan.contact_url} target="_blank" rel="noopener noreferrer" data-testid={`contact-${plan.id}`}
                      className="mt-6 rounded-md h-12 font-semibold gap-2 inline-flex items-center justify-center bg-[#1F7A4D] hover:bg-[#19633F] text-white transition-colors">
                      <MessageCircle className="w-4 h-4" /> Entrar em contato
                    </a>
                  ) : isFree ? (
                    <Link to="/login?signup=1" data-testid={`subscribe-${plan.id}`}
                      className="mt-6 rounded-md h-12 font-semibold gap-2 inline-flex items-center justify-center bg-ink hover:bg-ink/90 text-white transition-colors">
                      Começar grátis
                    </Link>
                  ) : (
                    <Button onClick={() => subscribe(plan)} disabled={loadingKey === lk}
                      data-testid={`subscribe-${plan.id}`}
                      className={`mt-6 rounded-md h-12 font-semibold gap-2 ${plan.highlight ? "bg-gold hover:bg-gold-hover text-ink" : "bg-ink hover:bg-ink/90 text-white"}`}>
                      {loadingKey === lk ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Assinar {plan.name}</>}
                    </Button>
                  )}
                  <ul className="mt-7 space-y-3">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${plan.highlight ? "text-gold" : "text-gold"}`} />
                        <span className={plan.highlight ? "text-cream/90" : "text-ink"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
        {plans.length > 0 && <PlanComparison plans={plans} />}
        <p className="text-center text-sm text-cappuccino mt-8 flex items-center justify-center gap-2">
          <ShieldCheck className="w-4 h-4 text-gold" /> Pagamento seguro via Stripe · Cartão de teste: 4242 4242 4242 4242
        </p>
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-2 gap-12 items-center">
          <img src={DOCTOR_IMG} alt="O Seu Chamador em funcionamento na TV da sala de espera" className="rounded-[1.5rem] shadow-xl w-full h-[360px] object-cover order-2 lg:order-1" />
          <div className="order-1 lg:order-2">
            <h2 className="font-heading text-3xl font-extrabold tracking-tight mb-6">Pronto em minutos</h2>
            <ol className="space-y-5">
              {["Crie a conta da clínica e cadastre as salas.",
                "Abra o painel em qualquer TV/tablet pelo navegador.",
                "A recepção adiciona os pacientes e chama com um clique.",
                "O nome aparece na TV e a voz anuncia a sala."].map((s, i) => (
                <li key={i} className="flex gap-4">
                  <span className="w-8 h-8 rounded-md bg-gold text-ink font-heading font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <p className="text-ink pt-1">{s}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-5 sm:px-8 py-16 scroll-mt-20">
        <div className="text-center mb-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Dúvidas frequentes</h2>
          <p className="text-cappuccino mt-3">O que as clínicas mais perguntam antes de começar.</p>
        </div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-list">
          {FAQ.map((item, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="bg-white border border-border rounded-2xl px-5">
              <AccordionTrigger className="font-heading font-semibold text-ink hover:no-underline text-left">{item.q}</AccordionTrigger>
              <AccordionContent className="text-cappuccino leading-relaxed">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Trial CTA banner */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-16">
        <div className="relative overflow-hidden rounded-[2rem] bg-ink text-cream p-10 sm:p-14 text-center">
          <div className="absolute inset-0 opacity-25" style={{ background: "radial-gradient(circle at 70% 20%, #C5A059 0%, transparent 50%)" }} />
          <div className="relative z-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">Comece hoje, sem cartão</h2>
            <p className="text-cream/70 max-w-xl mx-auto mb-8">Teste grátis por 14 dias. Chame o primeiro paciente em minutos e veja a diferença na sua sala de espera.</p>
            <Link to="/login?signup=1">
              <Button className="rounded-md h-12 px-8 bg-gold hover:bg-gold-hover text-ink text-base font-semibold gap-2 group" data-testid="cta-trial">
                Começar teste grátis
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-cappuccino">
        <BrandLogo className="h-10" testid="footer-brand-logo" />
        <p>© {new Date().getFullYear()} O Seu Chamador. Todos os direitos reservados.</p>
        <Link to="/login" className="text-gold font-semibold hover:underline">Acessar painel</Link>
      </footer>
    </div>
  );
}
